import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import {
  type CallAdapter,
  type CallAdapterMode,
  createCallAdapter,
  guestSessionQueryOptions,
  joinErrorInfo,
  type TrackSnapshot,
} from '@/entities/call'
import { MeetingRefs, meetingDetailQueryOptions } from '@/entities/meeting'
import { useSession } from '@/entities/session'
import { useJoinFlow } from '@/features/join-meeting'
import {
  BackgroundPicker,
  toBackgroundOption,
  useBackgroundSelection,
} from '@/features/set-background'
import {
  CameraPreview,
  DevicePicker,
  useDeviceSelection,
  useMediaDevices,
} from '@/features/setup-devices'
import { NoiseSuppressionToggle } from '@/features/suppress-noise'
import { runtimeConfig } from '@/shared/config'
import { formatMeetingStart } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { ZoneNote } from '@/shared/ui/zone-note'

export const Route = createFileRoute('/join/$id')({
  head: () => ({ meta: [{ title: 'Подключение · STIT Телемост' }] }),
  component: JoinPage,
})

const mode: CallAdapterMode = runtimeConfig.API_MODE === 'mock' ? 'fake' : 'real'

function JoinPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { session, isLoading: sessionLoading, isAuthenticated } = useSession()
  // Страница сотрудничья, но сюда может занести гостя (старая ссылка,
  // /room-редирект) — гостю на /login нельзя, ему нечем логиниться. Развилка:
  // SSO-сессия → prejoin; живой гостевой контекст → назад в гостевой флоу;
  // гостевой контекст завершённой встречи → честный статус; только «никто»
  // уходит на логин.
  const guestCtx = useQuery({
    ...guestSessionQueryOptions(),
    enabled: !sessionLoading && !isAuthenticated,
  })
  useEffect(() => {
    if (runtimeConfig.API_MODE === 'mock' || sessionLoading || isAuthenticated) return
    const r = guestCtx.data
    if (r === undefined) return // ждём вердикт по гостевой cookie (ретраи — тоже ожидание)
    if (r.kind === 'active') {
      void navigate({ to: '/guest/$token', params: { token: r.session.token }, replace: true })
      return
    }
    if (r.kind === 'ended' || r.kind === 'cancelled') return // статус-экран ниже
    void navigate({
      to: '/login',
      search: { redirect: window.location.pathname + window.location.search },
      replace: true,
    })
  }, [sessionLoading, isAuthenticated, guestCtx.data, navigate])
  const guestTerminal =
    !isAuthenticated && (guestCtx.data?.kind === 'ended' || guestCtx.data?.kind === 'cancelled')
      ? guestCtx.data.kind
      : null
  // Вердикт по гостевой cookie не получен (сеть/бэкенд недоступны): без него
  // непонятно, вести человека на логин или в гостевой флоу. Показать prejoin
  // нельзя — кнопка «Присоединиться» упрётся в 401 без объяснений.
  // В mock-режиме гейт не работает вовсе (там всегда есть сессия сценария).
  const anonymous =
    runtimeConfig.API_MODE !== 'mock' && !sessionLoading && !isAuthenticated && !guestTerminal
  const guestCtxFailed = anonymous && guestCtx.isError
  const guestCtxPending = anonymous && !guestCtx.isError && guestCtx.data === undefined
  // Гейт по состоянию встречи: ended/cancelled/не найдена — полноэкранный статус
  // вместо prejoin; поллинг детали сам заметит завершение, пока экран открыт.
  const detail = useQuery({ ...meetingDetailQueryOptions(id), enabled: session != null })
  const { cameras, microphones, speakers, error } = useMediaDevices()
  const camEnabled = useDeviceSelection((s) => s.camEnabled)
  const camId = useDeviceSelection((s) => s.camId)
  const selection = useBackgroundSelection((s) => s.selection)
  const join = useJoinFlow(id)

  const [previewTrack, setPreviewTrack] = useState<TrackSnapshot | null>(null)
  const previewAdapterRef = useRef<CallAdapter | null>(null)
  const genRef = useRef(0)

  // Превью на отдельном адаптере — только startLocalPreview, без connect;
  // на unmount поток освобождается, чтобы комната захватила камеру.
  // camId в зависимостях: выбор камеры обязан менять картинку, а не только стор.
  useEffect(() => {
    if (!camEnabled) return
    genRef.current += 1
    const gen = genRef.current
    let stop: (() => void) | null = null

    void createCallAdapter(mode)
      .then(async (a) => {
        const preview = await a.startLocalPreview({
          video: true,
          audio: false,
          ...(camId ? { deviceId: camId } : {}),
        })
        if (genRef.current !== gen) {
          preview.stop()
          void a.disconnect()
          return
        }
        previewAdapterRef.current = a
        // Применяем выбранный фон к превью, чтобы проверить его до входа.
        await a.setBackground(toBackgroundOption(useBackgroundSelection.getState().selection))
        stop = () => {
          preview.stop()
          void a.disconnect()
          previewAdapterRef.current = null
        }
        setPreviewTrack(preview.track)
      })
      // Камера занята другим приложением или доступ отозван: превью просто не
      // появится — вход в звонок это не блокирует.
      .catch(() => {})

    return () => {
      genRef.current += 1
      stop?.()
    }
  }, [camEnabled, camId])

  // Живое переключение фона в превью.
  useEffect(() => {
    void previewAdapterRef.current?.setBackground(toBackgroundOption(selection))
  }, [selection])

  // Закрытый звонок: сотрудник постучался и ждёт, пока его впустят. Опрос ведёт
  // useJoinFlow — как только допустят, он сам уводит в комнату.
  if (join.waiting) {
    return (
      <StatusScreen
        title="Ждём допуска"
        message="Звонок закрыт: кто-то из участников должен вас впустить. Экран откроется сам, как только это произойдёт."
      >
        <Spinner label="Ожидаем допуска" />
      </StatusScreen>
    )
  }

  // Аноним, чей гостевой контекст выяснить не удалось: prejoin ему показывать
  // нечестно — кнопка входа упрётся в отказ. Даём выбор: повторить или войти.
  if (guestCtxFailed) {
    return (
      <StatusScreen
        title="Не удалось проверить доступ"
        message="Мы не смогли выяснить, приглашены ли вы в эту встречу. Проверьте соединение и повторите — а если вы сотрудник, войдите в систему."
      >
        <Button onClick={() => void guestCtx.refetch()}>Повторить</Button>
        <Button
          variant="secondary"
          onClick={() =>
            void navigate({
              to: '/login',
              search: { redirect: window.location.pathname + window.location.search },
            })
          }
        >
          Войти
        </Button>
      </StatusScreen>
    )
  }

  // Пока вердикт по гостевой cookie не получен — ждём: мигать prejoin'ом, чтобы
  // потом увести на логин, хуже, чем короткий честный спиннер.
  if (guestCtxPending) {
    return (
      <StatusScreen title="Открываем встречу">
        <Spinner label="Проверяем доступ" />
      </StatusScreen>
    )
  }

  // Гость завершённой/отменённой встречи: честный финальный экран, не /login.
  if (guestTerminal) {
    return (
      <StatusScreen
        title={guestTerminal === 'ended' ? 'Встреча завершена' : 'Встреча отменена'}
        message={
          guestTerminal === 'ended'
            ? 'Встреча, в которой вы участвовали, уже закончилась. Если она продолжается по новой ссылке — попросите её у организатора.'
            : 'Организатор отменил встречу.'
        }
      />
    )
  }

  if (detail.isError) {
    const info = joinErrorInfo(detail.error)
    return (
      <StatusScreen
        title={info.kind === 'not-found' ? 'Встреча не найдена' : 'Не удалось загрузить встречу'}
        message={
          info.kind === 'not-found'
            ? 'Проверьте ссылку: возможно, встреча была удалена.'
            : 'Проверьте соединение и попробуйте ещё раз.'
        }
      >
        {info.kind !== 'not-found' && (
          <Button onClick={() => void detail.refetch()}>Повторить</Button>
        )}
        <Button variant="secondary" onClick={() => void navigate({ to: '/meetings' })}>
          К списку встреч
        </Button>
      </StatusScreen>
    )
  }
  if (detail.data?.state === 'ended') {
    return (
      <StatusScreen
        title="Встреча завершена"
        message={`«${detail.data.title}» уже закончилась. Запись и транскрипт доступны на странице встречи.`}
      >
        <Button onClick={() => void navigate({ to: '/meetings/$id', params: { id } })}>
          Открыть страницу встречи
        </Button>
        <Button variant="secondary" onClick={() => void navigate({ to: '/meetings' })}>
          К списку встреч
        </Button>
      </StatusScreen>
    )
  }
  if (detail.data?.state === 'cancelled') {
    return (
      <StatusScreen
        title="Встреча отменена"
        message={`Организатор отменил «${detail.data.title}».`}
      >
        <Button variant="secondary" onClick={() => void navigate({ to: '/meetings' })}>
          К списку встреч
        </Button>
      </StatusScreen>
    )
  }

  // Кнопки рендерятся дважды (desktop — под превью, mobile — в конце настроек):
  // видимый экземпляр всегда один, лишний скрыт брейкпоинтом.
  const actions = (
    <>
      <Button onClick={() => join.start()} disabled={join.isPending || join.waiting}>
        {join.isPending ? 'Подключение…' : 'Присоединиться'}
      </Button>
      <Button variant="secondary" onClick={() => void navigate({ to: '/meetings' })}>
        Отмена
      </Button>
    </>
  )

  return (
    <section className="container-x px-6 py-10">
      <PageHeader
        title={detail.data?.title ?? 'Подготовка к встрече'}
        // Prejoin — экран одиночного момента, и открывают его в том числе с
        // телефона, где бейдж зоны в шапке скрыт: зона называется здесь, со
        // смещением, а не подразумевается.
        description={
          detail.data?.state === 'scheduled' ? (
            <>
              Встреча начнётся с вашим входом
              {detail.data.scheduledAt && (
                <>
                  {` (запланирована на ${formatMeetingStart(detail.data.scheduledAt)} `}
                  <ZoneNote full />
                  {')'}
                </>
              )}
            </>
          ) : (
            'Проверьте камеру и микрофон перед входом'
          )
        }
      />

      {/* Куда именно человек заходит: компания и проект разговора. Заказчику —
          только проект: компания у него всегда своя. */}
      {detail.data && (
        <MeetingRefs
          meeting={detail.data}
          hideCompany={session?.kind === 'customer'}
          className="-mt-4 mb-6"
        />
      )}

      <div className="grid gap-6 md:grid-cols-[3fr_2fr] md:items-start">
        {/* Левая колонка sticky: превью + кнопки на экране, пока правая колонка
            (высокий пикер фонов) скроллится — фон живьём применяется к превью. */}
        <div className="flex flex-col gap-4 md:sticky md:top-6">
          <CameraPreview track={camEnabled ? previewTrack : null} name={session?.name ?? 'Вы'} />
          <div className="hidden gap-3 md:flex">{actions}</div>
        </div>

        <div className="flex flex-col gap-6">
          {error && <p className="text-accent">{error}</p>}
          <DevicePicker
            cameras={cameras}
            microphones={microphones}
            speakers={speakers}
            withChecks
          />
          <div className="flex flex-col gap-2">
            <span className="label">Фон камеры</span>
            <BackgroundPicker />
          </div>
          <NoiseSuppressionToggle />
          <div className="flex gap-3 md:hidden">{actions}</div>
        </div>
      </div>
    </section>
  )
}
